import React, { useState, useEffect } from 'react';
import { X, Cookie } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const CookieConsent = ({ onPrivacyClick, onCookiePolicyClick }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('elvextech-cookie-consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('elvextech-cookie-consent', JSON.stringify(allAccepted));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('elvextech-cookie-consent', JSON.stringify(essentialOnly));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    const savedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('elvextech-cookie-consent', JSON.stringify(savedPreferences));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleTogglePreference = (type) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center pointer-events-none">
      {/* Backdrop */}
      {showSettings && (
        <div 
          className="absolute inset-0 bg-black/50 pointer-events-auto"
          onClick={() => setShowSettings(false)}
        />
      )}

      {/* Cookie Banner */}
      <Card className="relative max-w-4xl w-full m-4 mb-6 pointer-events-auto shadow-2xl border-2">
        <div className="p-6">
          {!showSettings ? (
            // Main Banner
            <div>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Cookie className="w-6 h-6 text-cyan-600" />
                  <h3 className="text-xl font-bold text-navy">Cookie Preferences</h3>
                </div>
                <button 
                  onClick={handleAcceptEssential}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or accept only essential cookies.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={handleAcceptAll}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white flex-1"
                >
                  Accept All Cookies
                </Button>
                <Button 
                  onClick={handleAcceptEssential}
                  variant="outline"
                  className="flex-1 border-2 border-gray-300 hover:bg-gray-50"
                >
                  Essential Only
                </Button>
                <Button 
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="flex-1 border-2 border-gray-300 hover:bg-gray-50"
                >
                  Customize
                </Button>
              </div>

              <p className="text-sm text-gray-600 mt-4">
                Read our{' '}
                <button 
                  onClick={onPrivacyClick}
                  className="text-cyan-600 hover:underline cursor-pointer bg-transparent border-0 p-0"
                >
                  Privacy Policy
                </button>
                {' '}and{' '}
                <button 
                  onClick={onCookiePolicyClick}
                  className="text-cyan-600 hover:underline cursor-pointer bg-transparent border-0 p-0"
                >
                  Cookie Policy
                </button>
                {' '}for more information.
              </p>
            </div>
          ) : (
            // Cookie Settings
            <div>
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-xl font-bold text-navy">Customize Cookie Preferences</h3>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Essential Cookies */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-navy">Essential Cookies</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Always Active</span>
                      <div className="w-12 h-6 bg-cyan-500 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    These cookies are necessary for the website to function and cannot be disabled. 
                    They are usually only set in response to actions made by you such as setting your privacy preferences or filling in forms.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-navy">Analytics Cookies</h4>
                    <button
                      onClick={() => handleTogglePreference('analytics')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-cyan-500' : 'bg-gray-300'
                      } flex items-center ${preferences.analytics ? 'justify-end' : 'justify-start'} px-1`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. 
                    This helps us improve our website performance.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-navy">Marketing Cookies</h4>
                    <button
                      onClick={() => handleTogglePreference('marketing')}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        preferences.marketing ? 'bg-cyan-500' : 'bg-gray-300'
                      } flex items-center ${preferences.marketing ? 'justify-end' : 'justify-start'} px-1`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    These cookies track your online activity to help advertisers deliver more relevant advertising or to limit how many times you see an ad.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={handleSavePreferences}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white flex-1"
                >
                  Save Preferences
                </Button>
                <Button 
                  onClick={() => setShowSettings(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CookieConsent;
